import Tile from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { TileArcGISRest, XYZ } from "ol/source";
import { createManagedTileLoader } from "./tileLoader";

// Google satellite is sharded across mt0–mt3 (matching Google's own clients) so
// tile requests aren't funnelled through a single host.
const GOOGLE_SATELLITE_URLS = [0, 1, 2, 3].map(
  (i) => `https://mt${i}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}`,
);

// Builds a Tile layer over an XYZ source whose requests are managed by
// `createManagedTileLoader` (retry + Bearer auth + abort-on-hide). The
// `change:visible` hook cancels in-flight tiles when the basemap is switched
// away and re-fetches on return; it fires identically under the main viewer's
// ol-layerswitcher and the CaJ editor's radio control (both toggle visibility).
function createManagedXYZLayer({ url, urls, getToken }) {
  const loader = createManagedTileLoader(getToken ? { getToken } : {});
  const source = new XYZ({
    tileLoadFunction: loader.tileLoadFunction,
    ...(urls ? { urls } : { url }),
  });
  const layer = new Tile({ source });
  layer.on("change:visible", () =>
    loader.handleVisibility(layer.getVisible(), source),
  );
  return layer;
}

/**
 * The five basemap tile layers shared by the WKT viewer and CaJ editor:
 * OSM (no auth), Hillshade (ArcGIS, no auth), Satellite (Google, no auth),
 * Genesis (TomTom, Bearer), Orbis (TomTom, Bearer). Returned as
 * `[{ name, layer }, ...]` so each app can build its own UI (layerswitcher
 * vs. custom radio panel).
 */
export function createBasemapTileLayers(azureLoginControl) {
  const getToken = () => azureLoginControl.get("idToken");
  // Order matters — the WKT viewer's ol-layerswitcher renders radios in this
  // array order. Preserve [Hillshade, Satellite, Genesis, OSM, Orbis] to
  // match the layout the main viewer has shipped for a long time.
  return [
    {
      name: "Hillshade",
      layer: new Tile({
        source: new TileArcGISRest({
          url: "https://services.arcgisonline.com/arcgis/rest/services/Elevation/World_Hillshade/MapServer",
        }),
      }),
    },
    {
      name: "Satellite",
      layer: createManagedXYZLayer({ urls: GOOGLE_SATELLITE_URLS }),
    },
    {
      name: "Genesis",
      layer: createManagedXYZLayer({
        url:
          process.env.GENESIS_MAP_URL +
          "/map/1/tile/basic/main/{z}/{x}/{y}.png",
        getToken,
      }),
    },
    { name: "OSM", layer: new Tile({ source: new OSM() }) },
    {
      name: "Orbis",
      layer: createManagedXYZLayer({
        url:
          process.env.GENESIS_MAP_URL +
          "/maps/orbis/map-display/tile/{z}/{x}/{y}.png?apiVersion=1&tileSize=512",
        getToken,
      }),
    },
  ];
}
